import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  WorkOutlineOutlined as WorkOutlineOutlinedIcon,
  StartRounded as StartRoundedIcon,
  KeyboardTabRounded as KeyboardTabRoundedIcon,
  LogoutOutlined as LogoutOutlinedIcon,
  Apartment as ApartmentIcon,
  Explore as ExploreIcon,
  Business as BusinessIcon,
  PeopleAlt as PeopleAltIcon,
  AccountCircle as AccountCircleIcon,
  PriceChange as PriceChangeIcon,
  RequestQuote as RequestQuoteIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  LocalGroceryStore as LocalGroceryStoreIcon,
  Storefront as StorefrontIcon,
  Analytics as AnalyticsIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Construction as ConstructionIcon,
  HowToReg as HowToRegIcon,
  FactCheck as FactCheckIcon,
  ViewInAr as ViewIn3dIcon,
} from '@mui/icons-material';
import styled from 'styled-components';
import {
  DrawerBase as Drawer,
  DrawerBody,
  DrawerCategoryTitle,
  DrawerButton,
  DrawerControlButton,
  Logo,
  enqueueSnackbar,
} from '../../design-system';
import { useStore } from '../../context';
import { showToRolesMobile, showToRoles } from '../../utils';
import { useIsSmallScreen, useLogoutMutation } from '../../hooks';
import { MESSAGES } from '../../const';
import { i18n } from '../../i18n';

const DashboardDrawerBase = styled(Drawer)`
  grid-column: 1 / 2;
  grid-row: 1 / 3;

  height: 100%;

  @media print {
    visibility: hidden;
  }
`;

export const DashboardDrawer = ({
  isOpen,
  handleChangeDrawer,
}: {
  isOpen: boolean;
  handleChangeDrawer: () => void;
}) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mutate } = useLogoutMutation();
  const isSmallScreen = useIsSmallScreen();

  useEffect(() => {
    if (!isSmallScreen || !isOpen) return;

    handleChangeDrawer();
  }, [pathname]);

  const handleLogoutClick = async () => {
    await mutate(undefined, {
      onSuccess: () => {
        enqueueSnackbar(MESSAGES.AUTH.SUCCESS.LOG_OUT, 'success');
        navigate('/signin');
      },
    });
  };

  return (
    <DashboardDrawerBase id={'dashboard-drawer'} isOpen={isOpen}>
      <Logo isOpen={isOpen} handleChangeDrawer={handleChangeDrawer} />
      <DrawerBody>
        <DrawerCategoryTitle isOpen={isOpen}>
          {i18n.t('pages.dashboard.categoryDashboard')}
        </DrawerCategoryTitle>
        {showToRoles(
          [
            'SUPER_ADMIN',
            'YARD_COMPANY_ADMIN',
            'YARD_COMPANY_EMPLOYEE',
            'YARD_COMPANY_VIEWER',
            'YARD_MANAGER',
            'YARD_DISPATCHER',
            'VENDOR',
          ],
          user,
        ) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/overview'}
            startIcon={<DashboardIcon />}
            isActive={pathname.includes('/dashboard/overview')}
          >
            {i18n.t('button.overview')}
          </DrawerButton>
        )}
        <DrawerButton
          isOpen={isOpen}
          to={'/dashboard/containers'}
          startIcon={<WorkOutlineOutlinedIcon />}
          isActive={pathname.includes('/dashboard/containers')}
        >
          {i18n.t('button.containers')}
        </DrawerButton>
        {!showToRolesMobile(['TRADER'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/gateins'}
            startIcon={<KeyboardTabRoundedIcon />}
            isActive={pathname.includes('/dashboard/gateins')}
          >
            {i18n.t('button.gateInsDrawer')}
          </DrawerButton>
        )}
        {!showToRolesMobile(['TRADER'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/gateouts'}
            startIcon={<StartRoundedIcon />}
            isActive={pathname.includes('/dashboard/gateouts')}
          >
            {i18n.t('button.gateOutsDrawer')}
          </DrawerButton>
        )}
        {!showToRolesMobile(['TRADER'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/repairs'}
            startIcon={<ConstructionIcon />}
            isActive={pathname.includes('/dashboard/repairs')}
          >
            {i18n.t('button.repairs')}
          </DrawerButton>
        )}
        {showToRoles(
          ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_VIEWER', 'YARD_MANAGER'],
          user,
        ) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/inventory'}
            startIcon={<FactCheckIcon />}
            isActive={pathname.includes('/dashboard/inventory')}
          >
            {i18n.t('button.inventory')}
          </DrawerButton>
        )}
        {showToRoles('adminRead', user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/3d-places'}
            startIcon={<ViewIn3dIcon />}
            isActive={pathname.includes('/dashboard/3d-places')}
          >
            {i18n.t('button.3dPlaces')}
          </DrawerButton>
        )}
        {!showToRolesMobile(['TRADER'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/storage-pricing'}
            startIcon={<PriceChangeIcon />}
            isActive={pathname.includes('/dashboard/storage-pricing')}
          >
            {i18n.t('button.storagePricings')}
          </DrawerButton>
        )}
        {showToRoles('adminRead', user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/reports'}
            startIcon={<RequestQuoteIcon />}
            isActive={pathname.includes('/dashboard/reports')}
          >
            {i18n.t('button.reports')}
          </DrawerButton>
        )}
        {!showToRolesMobile(['TRADER'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/invoices'}
            startIcon={<AccountBalanceWalletIcon />}
            isActive={pathname.includes('/dashboard/invoices')}
          >
            {i18n.t('button.invoices')}
          </DrawerButton>
        )}
        {showToRoles(['SUPER_ADMIN'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/user-visits'}
            startIcon={<HowToRegIcon />}
            isActive={pathname.includes('/dashboard/user-visits')}
          >
            {i18n.t('button.userVisits')}
          </DrawerButton>
        )}
        {showToRoles(
          [
            'SUPER_ADMIN',
            'YARD_COMPANY_ADMIN',
            'YARD_COMPANY_VIEWER',
            'YARD_MANAGER',
            'CLIENT_MANAGER',
            'CLIENT_DISPATCHER',
          ],
          user,
        ) &&
          user.canTrade && (
            <>
              <DrawerCategoryTitle isOpen={isOpen}>
                {i18n.t('pages.dashboard.categoryTradeSales')}
              </DrawerCategoryTitle>
              <DrawerButton
                id={'drawer-button-marketplace'}
                isOpen={isOpen}
                to={'/dashboard/marketplace'}
                startIcon={<AddShoppingCartIcon />}
                isActive={pathname.includes('/dashboard/marketplace')}
              >
                {i18n.t('button.marketplace')}
              </DrawerButton>
              {!showToRolesMobile(['TRADER'], user) && (
                <DrawerButton
                  id={'drawer-button-managesales'}
                  isOpen={isOpen}
                  to={'/dashboard/managesales'}
                  startIcon={<StorefrontIcon />}
                  isActive={pathname.includes('/dashboard/managesales')}
                >
                  {i18n.t('button.manageSales')}
                </DrawerButton>
              )}
              <DrawerButton
                id={'drawer-button-cart'}
                isOpen={isOpen}
                to={'/dashboard/cart'}
                startIcon={<LocalGroceryStoreIcon />}
                isActive={pathname.includes('/dashboard/cart')}
              >
                {i18n.t('button.cart')}
              </DrawerButton>
              {showToRoles(['SUPER_ADMIN'], user) && (
                <DrawerButton
                  isOpen={isOpen}
                  to={'/dashboard/sale-stats'}
                  startIcon={<AnalyticsIcon />}
                  isActive={pathname.includes('/dashboard/sale-stats')}
                >
                  {i18n.t('button.saleStats')}
                </DrawerButton>
              )}
              {showToRoles('adminRead', user) && (
                <DrawerButton
                  id={'drawer-button-sales-fees'}
                  isOpen={isOpen}
                  to={'/dashboard/sales-fees'}
                  startIcon={<PriceChangeIcon />}
                  isActive={pathname.includes('/dashboard/sales-fees')}
                >
                  {i18n.t('button.salesFees')}
                </DrawerButton>
              )}
            </>
          )}
        {!showToRolesMobile(['TRADER'], user) && (
          <DrawerCategoryTitle isOpen={isOpen}>
            {i18n.t('pages.dashboard.categoryAccessControl')}
          </DrawerCategoryTitle>
        )}
        {showToRoles(['SUPER_ADMIN'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/yardcompanies'}
            startIcon={<ApartmentIcon />}
            isActive={pathname.includes('/dashboard/yardcompanies')}
          >
            {i18n.t('button.yardCompanies')}
          </DrawerButton>
        )}
        {showToRoles(['SUPER_ADMIN'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/yards'}
            startIcon={<ExploreIcon />}
            isActive={pathname.includes('/dashboard/yards')}
          >
            {i18n.t('button.yards')}
          </DrawerButton>
        )}
        {showToRoles('adminRead', user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/clients'}
            startIcon={<BusinessIcon />}
            isActive={pathname.includes('/dashboard/clients')}
          >
            {i18n.t('button.clientCompanies')}
          </DrawerButton>
        )}
        {showToRoles(
          [
            'SUPER_ADMIN',
            'YARD_COMPANY_ADMIN',
            'YARD_COMPANY_VIEWER',
            'YARD_MANAGER',
            'YARD_DISPATCHER',
            'CLIENT_MANAGER',
          ],
          user,
        ) &&
          !showToRolesMobile(['TRADER'], user) && (
            <DrawerButton
              isOpen={isOpen}
              to={'/dashboard/employees'}
              startIcon={<PeopleAltIcon />}
              isActive={pathname.includes('/dashboard/employees')}
            >
              {i18n.t('button.employees')}
            </DrawerButton>
          )}
        {!showToRolesMobile(['TRADER'], user) && (
          <DrawerButton
            isOpen={isOpen}
            to={'/dashboard/account'}
            startIcon={<AccountCircleIcon />}
            isActive={pathname.includes('/dashboard/account')}
          >
            {i18n.t('button.account')}
          </DrawerButton>
        )}
        <DrawerButton
          isOpen={isOpen}
          startIcon={<LogoutOutlinedIcon />}
          isActive={false}
          onClick={handleLogoutClick}
        >
          {i18n.t('button.logOut')}
        </DrawerButton>
      </DrawerBody>
      {!isSmallScreen && (
        <DrawerControlButton isOpen={isOpen} handleChangeDrawer={handleChangeDrawer} />
      )}
    </DashboardDrawerBase>
  );
};
